import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import {
  getSprint,
  getSprintMetrics,
  listTasks,
  PRIORITY_COLORS,
  SPRINT_STATUS_COLORS,
  type Sprint,
  type SprintMetricsSummary,
  type Task,
} from '../../../lib/tasks';

export const Route = createFileRoute('/kinerja/tasks/sprints/$sprintId_/summary')({
  component: SprintSummaryPage,
});

function SprintSummaryPage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <SprintSummaryContent />
    </ProtectedRoute>
  );
}

// ── Small stat card ────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color = 'text-gray-900' }: {
  label: string; value: string | number; sub?: string; color?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Ring progress ──────────────────────────────────────────────────────────────

function RingProgress({ pct, size = 56, stroke = 6, color = '#0d9488' }: {
  pct: number; size?: number; stroke?: number; color?: string;
}) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Main content ───────────────────────────────────────────────────────────────

function SprintSummaryContent() {
  const { sprintId } = Route.useParams();
  const navigate = useNavigate();

  const [sprint, setSprint] = useState<Sprint | null>(null);
  const [metrics, setMetrics] = useState<SprintMetricsSummary | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getSprint(sprintId),
      getSprintMetrics(sprintId),
      listTasks(sprintId),
    ])
      .then(([s, m, t]) => {
        if (cancelled) return;
        setSprint(s);
        setMetrics(m);
        setTasks(t);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load sprint');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sprintId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !sprint || !metrics) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <p className="text-red-600 text-sm">{error || 'Sprint not found'}</p>
      </div>
    );
  }

  const deliveryColor = metrics.deliveryRate >= 80 ? '#16a34a' : metrics.deliveryRate >= 50 ? '#d97706' : '#dc2626';
  const ticketColor = metrics.ticketCompletionRate >= 80 ? '#16a34a' : metrics.ticketCompletionRate >= 50 ? '#d97706' : '#dc2626';

  // Group recorded metrics by metric code → per user
  const metricsByCode: Record<string, { name: string; rows: typeof metrics.recordedMetrics }> = {};
  for (const r of metrics.recordedMetrics) {
    if (!metricsByCode[r.metricCode]) metricsByCode[r.metricCode] = { name: r.metricName, rows: [] };
    // Only keep the latest per user
    if (!metricsByCode[r.metricCode].rows.find((x) => x.userId === r.userId)) {
      metricsByCode[r.metricCode].rows.push(r);
    }
  }

  const doneTasks = tasks.filter((t) => t.status === 'done');
  const openTasks = tasks.filter((t) => t.status !== 'done');

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate({ to: '/kinerja/tasks/projects/$projectId' as any, params: { projectId: sprint.projectId } as any })}
          className="text-gray-400 hover:text-gray-600"
          title="Back to project"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900">{sprint.name}</h1>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SPRINT_STATUS_COLORS[sprint.status]}`}>
              {sprint.status}
            </span>
          </div>
          {sprint.goal && <p className="text-sm text-gray-500 mt-0.5">{sprint.goal}</p>}
          {(sprint.startDate || sprint.endDate) && (
            <p className="text-xs text-gray-400 mt-0.5">
              {sprint.startDate ? new Date(sprint.startDate).toLocaleDateString() : '?'}
              {' — '}
              {sprint.endDate ? new Date(sprint.endDate).toLocaleDateString() : '?'}
            </p>
          )}
        </div>
        {sprint.status !== 'completed' && (
          <button
            onClick={() => navigate({
              to: '/kinerja/tasks/sprints/$sprintId' as any,
              params: { sprintId } as any,
              search: { projectId: sprint.projectId } as any,
            })}
            className="px-3 py-1.5 text-sm text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 font-medium"
          >
            Open Board
          </button>
        )}
      </div>

      {/* Top-level KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Tasks" value={metrics.totalTasks} />
        <StatCard
          label="Done"
          value={metrics.doneTasks}
          sub={`${metrics.totalTasks - metrics.doneTasks} remaining`}
          color="text-teal-700"
        />
        <StatCard
          label="Story Points"
          value={metrics.velocity}
          sub="completed velocity"
          color="text-blue-700"
        />
        <StatCard
          label="On-time Rate"
          value={`${metrics.ticketCompletionRate}%`}
          color={metrics.ticketCompletionRate >= 80 ? 'text-green-700' : metrics.ticketCompletionRate >= 50 ? 'text-amber-700' : 'text-red-600'}
        />
      </div>

      {/* Progress rings */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-5">
          <div className="relative">
            <RingProgress pct={metrics.deliveryRate} size={72} stroke={7} color={deliveryColor} />
            <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-gray-700">
              {metrics.deliveryRate}%
            </span>
          </div>
          <div>
            <p className="font-semibold text-gray-800">Delivery Rate</p>
            <p className="text-xs text-gray-500 mt-0.5">Tasks completed out of total</p>
            <div className="flex gap-3 mt-2 text-xs">
              <span className="text-gray-600">Todo: <b>{metrics.statusCounts['todo'] ?? 0}</b></span>
              <span className="text-blue-600">In Progress: <b>{metrics.statusCounts['in_progress'] ?? 0}</b></span>
              <span className="text-purple-600">In Review: <b>{metrics.statusCounts['in_review'] ?? 0}</b></span>
              <span className="text-green-600">Done: <b>{metrics.statusCounts['done'] ?? 0}</b></span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-5">
          <div className="relative">
            <RingProgress pct={metrics.ticketCompletionRate} size={72} stroke={7} color={ticketColor} />
            <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-gray-700">
              {metrics.ticketCompletionRate}%
            </span>
          </div>
          <div>
            <p className="font-semibold text-gray-800">On-time Completion</p>
            <p className="text-xs text-gray-500 mt-0.5">Done tasks completed before due date</p>
            <p className="text-xs text-gray-400 mt-2">
              {doneTasks.filter((t) => {
                if (!t.dueDate || !t.completedAt) return true;
                return new Date(t.completedAt) <= new Date(t.dueDate);
              }).length} of {doneTasks.length} done tasks on time
            </p>
          </div>
        </div>
      </div>

      {/* Per-assignee breakdown */}
      {metrics.byAssignee.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Team Performance</h2>
            <p className="text-xs text-gray-500 mt-0.5">Per-assignee breakdown for this sprint</p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Member</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Tasks</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Done</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Delivery</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Velocity</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">On-time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {metrics.byAssignee
                .sort((a, b) => b.deliveryRate - a.deliveryRate)
                .map((a) => (
                  <tr key={a.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center text-xs font-semibold text-teal-700 shrink-0">
                          {a.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-gray-800">{a.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{a.totalTasks}</td>
                    <td className="px-4 py-3 text-right text-teal-700 font-medium">{a.doneTasks}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold ${a.deliveryRate >= 80 ? 'text-green-700' : a.deliveryRate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {a.deliveryRate}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-blue-700 font-medium">{a.velocity} pts</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold ${a.ticketCompletionRate >= 80 ? 'text-green-700' : a.ticketCompletionRate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {a.ticketCompletionRate}%
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recorded metric snapshots */}
      {Object.keys(metricsByCode).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Recorded Performance Metrics</h2>
            <p className="text-xs text-gray-500 mt-0.5">Metrics written to employee profiles at sprint completion</p>
          </div>
          <div className="divide-y divide-gray-100">
            {Object.entries(metricsByCode).map(([code, { name, rows }]) => (
              <div key={code} className="px-5 py-4">
                <p className="text-sm font-semibold text-gray-700 mb-3">
                  <span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded mr-2">{code}</span>
                  {name}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {rows.sort((a, b) => b.value - a.value).map((r) => {
                    const maxVal = Math.max(...rows.map((x) => x.value), 1);
                    const pct = (r.value / maxVal) * 100;
                    return (
                      <div key={r.userId} className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-[10px] font-bold text-teal-700 shrink-0">
                          {r.userName.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-700 truncate">{r.userName.split(' ')[0]}</span>
                            <span className="text-xs font-semibold text-gray-800 ml-1">{r.value}</span>
                          </div>
                          <div className="h-1 bg-gray-100 rounded-full mt-0.5">
                            <div className="h-full bg-teal-500 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Task list — done */}
      {doneTasks.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Completed Tasks</h2>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{doneTasks.length}</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {doneTasks.map((t) => (
              <li key={t.id} className="px-5 py-3 flex items-center gap-3">
                <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="flex-1 text-sm text-gray-700">{t.title}</span>
                {t.storyPoints !== null && (
                  <span className="text-xs text-gray-400 font-mono">{t.storyPoints} pts</span>
                )}
                {t.priority && (
                  <span className={`text-xs px-1.5 py-0.5 rounded ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Task list — open/incomplete */}
      {openTasks.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Incomplete Tasks</h2>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{openTasks.length}</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {openTasks.map((t) => (
              <li key={t.id} className="px-5 py-3 flex items-center gap-3">
                <svg className="w-4 h-4 text-gray-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="flex-1 text-sm text-gray-600">{t.title}</span>
                <span className="text-xs text-gray-400 capitalize">{t.status.replace('_', ' ')}</span>
                {t.storyPoints !== null && (
                  <span className="text-xs text-gray-400 font-mono">{t.storyPoints} pts</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
